import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Printer } from "lucide-react";
import { useInvoices } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { Has } from "@/components/Has";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { InvoiceStatusBadge } from "@/components/StatusBadge";
import { InvoiceForm } from "@/features/billing/InvoiceForm";
import { fmtINR, fmtDate } from "@emr/shared/india";

export default function BillingPage() {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("billing.create");
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading } = useInvoices();
  const rows = (data?.rows ?? []).filter(
    (i) => !q || (i.patientName ?? "").toLowerCase().includes(q.toLowerCase()) || i.number.toLowerCase().includes(q.toLowerCase()) || (i.clinicUhid ?? "").toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Billing</h1>
        <Has perm="billing.create">
          <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => setFormOpen(true)}>
            <Plus size={16} className="mr-1" /> New Invoice
          </Button>
        </Has>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <Input placeholder="Search by invoice #, patient, UHID…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                <TableHead>Invoice #</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>UHID</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6}><Skeleton className="h-10" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted">No invoices yet.</TableCell></TableRow>
              ) : (
                rows.map((inv) => (
                  <TableRow
                    key={inv.id}
                    className="cursor-pointer even:bg-cream-50/50"
                    onClick={() => navigate(`/billing/${inv.id}`)}
                  >
                    <TableCell className="font-mono text-xs">{inv.number}</TableCell>
                    <TableCell className="font-medium">{inv.patientName ?? "—"}</TableCell>
                    <TableCell className="text-xs text-muted">{inv.clinicUhid ?? "—"}</TableCell>
                    <TableCell className="text-xs">{fmtDate(inv.date)}</TableCell>
                    <TableCell className="text-right font-medium">{fmtINR(inv.totalPaise)}</TableCell>
                    <TableCell><InvoiceStatusBadge status={inv.status} /></TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <InvoiceForm open={formOpen} onOpenChange={setFormOpen} onCreated={(id) => navigate(`/billing/${id}`)} />
    </div>
  );
}
